/*
 * /admin (page)
 */
import {
	Badge,
	Button,
	Container,
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
	Form,
	InputFormField,
	PageHeader,
	PageWithEffect,
	SelectFormField,
	toast,
} from "@emr/react-ui"
import type { AuthUser, UserRole } from "@emr/types"
import { getPlatformMetadata } from "@emr/utils"
import { createFileRoute } from "@tanstack/react-router"
import {
	CheckCircle,
	Clock,
	Copy,
	Loader2,
	MoreHorizontal,
	Plus,
	ShieldOff,
	Trash2,
	UserCheck,
	UserCog,
	UserX,
} from "lucide-react"
import { useState } from "react"
import { z } from "zod"
import { trpc } from "../../trpc"
import { adminPageLoader } from "../../utils/auth.utils"

export const Route = createFileRoute("/auth/admin")({
	component: AdminPage,
	loader: adminPageLoader,
})

const ROLE_OPTIONS = [
	{ label: "Super Admin", value: "super_admin" },
	{ label: "Admin", value: "admin" },
	{ label: "Practitioner", value: "practitioner" },
	{ label: "Nurse", value: "nurse" },
	{ label: "Patient", value: "patient" },
]

const STAFF_ROLE_OPTIONS = ROLE_OPTIONS.filter((r) => r.value !== "patient")

const ROLE_VARIANT: Record<
	UserRole,
	"default" | "secondary" | "outline" | "destructive"
> = {
	super_admin: "default",
	admin: "default",
	practitioner: "secondary",
	nurse: "secondary",
	patient: "outline",
	guest: "outline",
}

const ROLE_LABEL: Record<UserRole, string> = {
	super_admin: "Super Admin",
	admin: "Admin",
	practitioner: "Practitioner",
	nurse: "Nurse",
	patient: "Patient",
	guest: "Guest",
}

function isUserBanned(user: AuthUser): boolean {
	const banned = (user as any).banned_until
	if (!banned) return false
	return new Date(banned) > new Date()
}

/* ─── Page ───────────────────────────────────────────────────────────────── */

function AdminPage() {
	const [tab, setTab] = useState<"users" | "pending">("users")
	const {
		data: users = [],
		isLoading,
		error,
		refetch,
	} = trpc.user.listUsers.useQuery()

	const pendingUsers = (users ?? []).filter((u) => !getPlatformMetadata(u)?.joined)
	const staffUsers = (users ?? []).filter((u) => getPlatformMetadata(u)?.role !== "patient")

	const [createOpen, setCreateOpen] = useState(false)
	const [resetLink, setResetLink] = useState<{ email: string; link: string } | null>(null)
	const [editRoleUser, setEditRoleUser] = useState<AuthUser | null>(null)
	const [editInfoUser, setEditInfoUser] = useState<AuthUser | null>(null)
	const [approveUser, setApproveUser] = useState<AuthUser | null>(null)

	const setRole = trpc.user.setUserRole.useMutation({
		onSuccess: () => { toast.success("Role updated"); refetch(); setEditRoleUser(null) },
		onError: (e) => toast.error(e.message),
	})
	const deactivate = trpc.user.deactivateUser.useMutation({
		onSuccess: () => { toast.success("User deactivated"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const reactivate = trpc.user.reactivateUser.useMutation({
		onSuccess: () => { toast.success("User reactivated"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const resetPassword = trpc.user.resetUserPassword.useMutation({
		onSuccess: (data) => setResetLink({ email: data.email, link: data.resetLink }),
		onError: (e) => toast.error(e.message),
	})
	const deleteUser = trpc.user.deleteUser.useMutation({
		onSuccess: () => { toast.success("User deleted"); refetch() },
		onError: (e) => toast.error(e.message),
	})
	const updateInfo = trpc.user.updateUserInfo.useMutation({
		onSuccess: () => { toast.success("User updated"); refetch(); setEditInfoUser(null) },
		onError: (e) => toast.error(e.message),
	})

	return (
		<PageWithEffect effect={"aurora"} centerVertical={false}>
			<Container width={"xxl"} padding={"lg"} gap={"lg"}>
				<PageHeader
					title={"User Management"}
					right={{
						Component: tab === "users" ? (
							<Button onClick={() => setCreateOpen(true)} size="sm">
								<Plus className="mr-1.5 size-4" />
								Create User
							</Button>
						) : undefined,
					}}
				/>

				{/* Tab bar */}
				<div className="flex gap-0 border-b">
					{([
						{ id: "users" as const, label: "All Users" },
						{ id: "pending" as const, label: "Pending", count: pendingUsers.length },
					]).map(({ id, label, count }) => (
						<button
							key={id}
							onClick={() => setTab(id)}
							className={`flex items-center gap-2 px-5 py-3 text-sm font-medium transition-colors whitespace-nowrap relative hover:text-foreground ${
								tab === id
									? "text-primary after:absolute after:bottom-0 after:left-0 after:right-0 after:h-[2px] after:bg-primary after:rounded-t-full"
									: "text-muted-foreground"
							}`}
						>
							{id === "pending" && <Clock className="w-4 h-4" />}
							{label}
							{count !== undefined && count > 0 && (
								<span className="ml-1 rounded-full bg-amber-100 px-1.5 py-0.5 text-xs font-semibold text-amber-700">
									{count}
								</span>
							)}
						</button>
					))}
				</div>

				{isLoading && (
					<div className={"flex items-center justify-center py-16"}>
						<Loader2 className={"size-6 animate-spin text-muted-foreground"} />
					</div>
				)}

				{error && (
					<div className={"py-8 text-center text-sm text-destructive"}>
						{error.message}
					</div>
				)}

				{!isLoading && !error && tab === "users" && (
					<UserTable
						users={staffUsers}
						onEditRole={setEditRoleUser}
						onEditInfo={setEditInfoUser}
						onResetPassword={(u) => resetPassword.mutate({ userId: u.id })}
						onDeactivate={(u) => deactivate.mutate({ userId: u.id })}
						onReactivate={(u) => reactivate.mutate({ userId: u.id })}
						onDelete={(u) => deleteUser.mutate({ userId: u.id })}
						pendingId={
							deactivate.isPending ? deactivate.variables?.userId
							: reactivate.isPending ? reactivate.variables?.userId
							: resetPassword.isPending ? resetPassword.variables?.userId
							: deleteUser.isPending ? deleteUser.variables?.userId
							: undefined
						}
					/>
				)}

				{!isLoading && !error && tab === "pending" && (
					<PendingTable
						users={pendingUsers}
						onApprove={setApproveUser}
					/>
				)}
			</Container>

			<CreateUserDialog open={createOpen} onOpenChange={setCreateOpen} onSuccess={() => { refetch(); setCreateOpen(false) }} />

			{editRoleUser && (
				<EditRoleDialog
					user={editRoleUser}
					open={!!editRoleUser}
					onOpenChange={(o) => !o && setEditRoleUser(null)}
					onSave={(role) => setRole.mutate({ userId: editRoleUser.id, role: role as any })}
					isPending={setRole.isPending}
				/>
			)}

			{editInfoUser && (
				<EditInfoDialog
					user={editInfoUser}
					open={!!editInfoUser}
					onOpenChange={(o) => !o && setEditInfoUser(null)}
					onSave={(data) => updateInfo.mutate({ userId: editInfoUser.id, ...data })}
					isPending={updateInfo.isPending}
				/>
			)}

			{approveUser && (
				<ApproveDialog
					user={approveUser}
					open={!!approveUser}
					onOpenChange={(o) => !o && setApproveUser(null)}
					onSuccess={() => { refetch(); setApproveUser(null) }}
				/>
			)}

			{resetLink && (
				<ResetLinkDialog
					email={resetLink.email}
					link={resetLink.link}
					open={!!resetLink}
					onOpenChange={(o) => !o && setResetLink(null)}
				/>
			)}
		</PageWithEffect>
	)
}

/* ─── User Table ──────────────────────────────────────────────────────────── */

type UserTableProps = {
	users: AuthUser[]
	onEditRole: (u: AuthUser) => void
	onEditInfo: (u: AuthUser) => void
	onResetPassword: (u: AuthUser) => void
	onDeactivate: (u: AuthUser) => void
	onReactivate: (u: AuthUser) => void
	onDelete: (u: AuthUser) => void
	pendingId?: string
}

function UserTable({
	users,
	onEditRole,
	onEditInfo,
	onResetPassword,
	onDeactivate,
	onReactivate,
	onDelete,
	pendingId,
}: UserTableProps) {
	if (users.length === 0) {
		return (
			<p className={"py-12 text-center text-sm text-muted-foreground"}>
				No users found.
			</p>
		)
	}

	return (
		<div className={"w-full overflow-x-auto rounded-lg border"}>
			<table className={"w-full text-sm"}>
				<thead>
					<tr className={"border-b bg-muted/40 text-left"}>
						<th className={"px-4 py-3 font-medium"}>Name / Email</th>
						<th className={"px-4 py-3 font-medium"}>Role</th>
						<th className={"px-4 py-3 font-medium"}>Status</th>
						<th className={"px-4 py-3 font-medium"}>Joined</th>
						<th className={"px-4 py-3 font-medium"} />
					</tr>
				</thead>
				<tbody>
					{users.map((u) => {
						const meta = getPlatformMetadata(u)
						const role = meta?.role as UserRole | undefined
						const banned = isUserBanned(u)
						const isPending = pendingId === u.id
						const displayName = u.user_metadata?.displayName ?? u.email?.split("@")[0]
						const joinedDate = meta?.joined
							? new Date(meta.joined).toLocaleDateString()
							: new Date(u.created_at).toLocaleDateString()

						return (
							<tr
								key={u.id}
								className={"border-b last:border-0 hover:bg-muted/20"}
							>
								<td className={"px-4 py-3"}>
									<p className="font-medium text-foreground">{displayName}</p>
									<p className="text-xs text-muted-foreground">{u.email}</p>
								</td>
								<td className={"px-4 py-3"}>
									{role ? (
										<Badge variant={ROLE_VARIANT[role]} className={"text-xs"}>
											{ROLE_LABEL[role]}
										</Badge>
									) : (
										<Badge variant={"outline"} className={"text-xs"}>
											Waitlist
										</Badge>
									)}
								</td>
								<td className={"px-4 py-3"}>
									{banned ? (
										<Badge variant={"destructive"} className={"text-xs"}>
											Inactive
										</Badge>
									) : (
										<Badge variant={"secondary"} className={"text-xs"}>
											Active
										</Badge>
									)}
								</td>
								<td className={"px-4 py-3 text-muted-foreground"}>{joinedDate}</td>
								<td className={"px-4 py-3 text-right"}>
									{isPending ? (
										<Loader2 className="size-4 animate-spin text-muted-foreground" />
									) : (
										<DropdownMenu>
											<DropdownMenuTrigger asChild>
												<Button variant="ghost" size="icon" className="size-8">
													<MoreHorizontal className="size-4" />
												</Button>
											</DropdownMenuTrigger>
											<DropdownMenuContent align="end">
												<DropdownMenuItem onClick={() => onEditInfo(u)}>
													<UserCog className="mr-2 size-4" />
													Edit Info
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => onEditRole(u)}>
													<ShieldOff className="mr-2 size-4" />
													Change Role
												</DropdownMenuItem>
												<DropdownMenuItem onClick={() => onResetPassword(u)}>
													<CheckCircle className="mr-2 size-4" />
													Reset Password
												</DropdownMenuItem>
												<DropdownMenuSeparator />
												{banned ? (
													<DropdownMenuItem onClick={() => onReactivate(u)}>
														<CheckCircle className="mr-2 size-4" />
														Reactivate
													</DropdownMenuItem>
												) : (
													<DropdownMenuItem
														onClick={() => onDeactivate(u)}
														className="text-amber-600"
													>
														<UserX className="mr-2 size-4" />
														Deactivate
													</DropdownMenuItem>
												)}
												<DropdownMenuSeparator />
												<DropdownMenuItem
													onClick={() => onDelete(u)}
													className="text-destructive"
												>
													<Trash2 className="mr-2 size-4" />
													Delete User
												</DropdownMenuItem>
											</DropdownMenuContent>
										</DropdownMenu>
									)}
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

/* ─── Pending Table ──────────────────────────────────────────────────────── */

function PendingTable({
	users,
	onApprove,
}: {
	users: AuthUser[]
	onApprove: (u: AuthUser) => void
}) {
	if (users.length === 0) {
		return (
			<p className={"py-12 text-center text-sm text-muted-foreground"}>
				No pending patients — nobody on the waitlist right now.
			</p>
		)
	}

	return (
		<div className={"w-full overflow-x-auto rounded-lg border"}>
			<table className={"w-full text-sm"}>
				<thead>
					<tr className={"border-b bg-muted/40 text-left"}>
						<th className={"px-4 py-3 font-medium"}>Name / Email</th>
						<th className={"px-4 py-3 font-medium"}>Signed Up</th>
						<th className={"px-4 py-3 font-medium"} />
					</tr>
				</thead>
				<tbody>
					{users.map((u) => {
						const displayName = u.user_metadata?.displayName ?? u.email?.split("@")[0]
						const signedUp = new Date(u.created_at).toLocaleDateString("en-CA", {
							year: "numeric", month: "short", day: "numeric",
						})
						return (
							<tr key={u.id} className={"border-b last:border-0 hover:bg-muted/20"}>
								<td className={"px-4 py-3"}>
									<p className="font-medium text-foreground">{displayName}</p>
									<p className="text-xs text-muted-foreground">{u.email}</p>
								</td>
								<td className={"px-4 py-3 text-muted-foreground"}>{signedUp}</td>
								<td className={"px-4 py-3 text-right"}>
									<Button size="sm" onClick={() => onApprove(u)}>
										<UserCheck className="mr-1.5 size-4" />
										Approve
									</Button>
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

/* ─── Approve Dialog ─────────────────────────────────────────────────────── */

const approveSchema = z.object({
	role: z.enum(["patient", "practitioner", "nurse", "admin", "super_admin"]),
})
type ApproveValues = z.infer<typeof approveSchema>

function ApproveDialog({
	user,
	open,
	onOpenChange,
	onSuccess,
}: {
	user: AuthUser
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const approve = trpc.user.approveUser.useMutation({
		onSuccess: () => { toast.success("User approved"); onSuccess() },
		onError: (e) => toast.error(e.message),
	})

	const displayName = user.user_metadata?.displayName ?? user.email

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>Approve User</DialogTitle>
					<p className="text-sm text-muted-foreground">{displayName}</p>
				</DialogHeader>
				<Form<ApproveValues>
					schema={approveSchema}
					defaultValues={{ role: "patient" }}
					onSubmit={async (v) => {
						try {
							await approve.mutateAsync({ userId: user.id, role: v.role })
						} catch (e: any) {
							return e?.message ?? "An error occurred"
						}
					}}
					className="flex flex-col gap-4"
				>
					<SelectFormField
						name="role"
						label="Approve as"
						options={ROLE_OPTIONS}
						placeholder="Select role"
					/>
					<DialogFooter>
						<Button type="submit" disabled={approve.isPending}>
							{approve.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
							Approve
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Create User Dialog ─────────────────────────────────────────────────── */

const createUserSchema = z.object({
	displayName: z.string().min(1, "Name is required"),
	email: z.string().email("Valid email required"),
	role: z.enum(["practitioner", "nurse", "admin", "super_admin"]),
})

type CreateUserValues = z.infer<typeof createUserSchema>

function CreateUserDialog({
	open,
	onOpenChange,
	onSuccess,
}: {
	open: boolean
	onOpenChange: (o: boolean) => void
	onSuccess: () => void
}) {
	const createUser = trpc.user.createStaffUser.useMutation({
		onSuccess: () => {
			toast.success("User created. They can use 'Forgot Password' to set their password.")
			onSuccess()
		},
		onError: (e) => toast.error(e.message),
	})

	async function handleSubmit(values: CreateUserValues) {
		try {
			await createUser.mutateAsync(values)
		} catch (e: any) {
			return e?.message ?? "An error occurred"
		}
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Create Staff User</DialogTitle>
				</DialogHeader>
				<Form<CreateUserValues>
					schema={createUserSchema}
					onSubmit={handleSubmit}
					resetOnSuccess
					className="flex flex-col gap-4"
				>
					<InputFormField name="displayName" label="Full Name" placeholder="Jane Smith" />
					<InputFormField name="email" label="Email" placeholder="jane@example.com" />
					<SelectFormField
						name="role"
						label="Role"
						options={STAFF_ROLE_OPTIONS}
						placeholder="Select role"
					/>
					<DialogFooter>
						<Button type="submit" disabled={createUser.isPending}>
							{createUser.isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
							Create User
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Edit Role Dialog ───────────────────────────────────────────────────── */

const editRoleSchema = z.object({
	role: z.enum(["patient", "practitioner", "nurse", "admin", "super_admin"]),
})

type EditRoleValues = z.infer<typeof editRoleSchema>

function EditRoleDialog({
	user,
	open,
	onOpenChange,
	onSave,
	isPending,
}: {
	user: AuthUser
	open: boolean
	onOpenChange: (o: boolean) => void
	onSave: (role: string) => void
	isPending: boolean
}) {
	const currentRole = getPlatformMetadata(user)?.role as UserRole | undefined

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>Change Role</DialogTitle>
					<p className="text-sm text-muted-foreground">
						{user.user_metadata?.displayName ?? user.email}
					</p>
				</DialogHeader>
				<Form<EditRoleValues>
					schema={editRoleSchema}
					defaultValues={{ role: currentRole as any }}
					onSubmit={async (v) => onSave(v.role)}
					className="flex flex-col gap-4"
				>
					<SelectFormField
						name="role"
						label="Role"
						options={ROLE_OPTIONS}
						placeholder="Select role"
					/>
					<DialogFooter>
						<Button type="submit" disabled={isPending}>
							{isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
							Save Role
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Edit Info Dialog ───────────────────────────────────────────────────── */

const editInfoSchema = z.object({
	displayName: z.string().min(1, "Name is required"),
	email: z.string().email("Valid email required"),
})

type EditInfoValues = z.infer<typeof editInfoSchema>

function EditInfoDialog({
	user,
	open,
	onOpenChange,
	onSave,
	isPending,
}: {
	user: AuthUser
	open: boolean
	onOpenChange: (o: boolean) => void
	onSave: (data: { displayName: string; email: string }) => void
	isPending: boolean
}) {
	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-sm">
				<DialogHeader>
					<DialogTitle>Edit User Info</DialogTitle>
				</DialogHeader>
				<Form<EditInfoValues>
					schema={editInfoSchema}
					defaultValues={{
						displayName: user.user_metadata?.displayName ?? "",
						email: user.email ?? "",
					}}
					onSubmit={async (v) => onSave(v)}
					className="flex flex-col gap-4"
				>
					<InputFormField name="displayName" label="Full Name" placeholder="Jane Smith" />
					<InputFormField name="email" label="Email" placeholder="jane@example.com" />
					<DialogFooter>
						<Button type="submit" disabled={isPending}>
							{isPending && <Loader2 className="mr-2 size-4 animate-spin" />}
							Save Changes
						</Button>
					</DialogFooter>
				</Form>
			</DialogContent>
		</Dialog>
	)
}

/* ─── Reset Link Dialog ──────────────────────────────────────────────────── */

function ResetLinkDialog({
	email,
	link,
	open,
	onOpenChange,
}: {
	email: string
	link: string
	open: boolean
	onOpenChange: (o: boolean) => void
}) {
	function copyLink() {
		navigator.clipboard.writeText(link)
		toast.success("Link copied to clipboard")
	}

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="sm:max-w-md">
				<DialogHeader>
					<DialogTitle>Password Reset Link</DialogTitle>
					<p className="text-sm text-muted-foreground">
						Share this link with <strong>{email}</strong> to let them set a new password.
						This link expires after use.
					</p>
				</DialogHeader>
				<div className="flex items-center gap-2 rounded-md border bg-muted/40 p-3">
					<p className="flex-1 truncate text-xs text-muted-foreground">{link}</p>
					<Button variant="ghost" size="icon" className="shrink-0" onClick={copyLink}>
						<Copy className="size-4" />
					</Button>
				</div>
				<DialogFooter>
					<Button variant="outline" onClick={() => onOpenChange(false)}>
						Close
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	)
}
